import { Button, Label, Slider } from '@design-system';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

import { X } from 'lucide-react';

import { cn } from '@/lib';
import { isEqual } from 'lodash';

interface CustomRangeSliderProps {
  name: string;
  label?: string;
  control: Control<any>;
  size?: 'sm' | 'default';
  testId?: string;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number[];
  showTooltip?: boolean;
  tooltipContent?: (value: number) => React.ReactNode;
  onValueChange?: (value: number[]) => void;
}

export function CustomRangeSlider({
  name,
  control,
  label,
  size = 'default',
  testId,
  min = 0,
  max = 100,
  step,
  defaultValue,
  onValueChange,
  ...props
}: CustomRangeSliderProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const valueArray = Array.isArray(value) ? value : [0, 100];
        const isDefault = isEqual(valueArray, defaultValue);

        return (
          <div className="space-y-4">
            <div
              className={cn('flex flex-wrap items-center justify-between gap-4', {
                'gap-2': size === 'sm',
              })}
            >
              {label && (
                <Label
                  className={cn('text-primary-dark text-lg font-semibold', {
                    'text-sm': size === 'sm',
                  })}
                >
                  {label}
                </Label>
              )}
              {!isDefault && (
                <Button
                  size={size === 'sm' ? 'icon' : 'sm'}
                  variant={size === 'sm' ? 'ghost' : 'outline'}
                  onClick={() => onChange(defaultValue)}
                  className={cn('ml-auto h-7 px-2 text-xs', {
                    'size-5 p-0': size === 'sm',
                    'invisible': isDefault,
                  })}
                >
                  {size === 'sm' ? <X className="size-4" /> : 'Reset'}
                </Button>
              )}
            </div>
            <Slider
              data-testid={testId}
              value={valueArray}
              min={min}
              max={max}
              step={step}
              onValueChange={newValue => {
                onValueChange?.(newValue);
                onChange(newValue);
              }}
              className="**:data-[slot=slider-thumb]:size-4 **:data-[slot=slider-track]:h-1.5"
            />
          </div>
        );
      }}
    />
  );
}
